// src/scene/useStageStore.ts
/**
 * Stage Controller Store (Zustand)
 *
 * Single source of truth for scroll/stage state.
 * Provides deterministic, smooth stage transitions.
 * Exposes derived values for camera poses and stage activation.
 */

import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import { useShallow } from 'zustand/react/shallow'
import { Vector3 } from 'three'

// ============================================================================
// STAGE DEFINITIONS
// ============================================================================

export interface StageAnchor {
  id: string
  name: string
  // Camera pose
  cameraPosition: [number, number, number]
  cameraTarget: [number, number, number]
  // Avatar pose (optional)
  avatarPosition?: [number, number, number]
  avatarRotation?: number
  // Panel position (optional, for reference)
  panelPosition?: [number, number, number]
  panelRotation?: [number, number, number]
}

// Default stage configuration
// Avatar stays FIXED at origin - only camera moves between stages
// This ensures consistent lighting on the character
// Panels spread FAR apart so only active one is in camera frame
const AVATAR_POSITION: [number, number, number] = [0, -0.82, 1.5]
const AVATAR_ROTATION = 0 // Facing +Z (toward default camera)

export const STAGE_ANCHORS: StageAnchor[] = [
  {
    id: 'intro',
    name: 'Welcome',
    cameraPosition: [0, 0.7, 3.5],
    cameraTarget: [0, 0.2, 1.5],
    avatarPosition: AVATAR_POSITION,
    avatarRotation: AVATAR_ROTATION,
    panelPosition: [0, 0.15, 2],
    panelRotation: [-0.15, 0, 0],
  },
]

// ============================================================================
// STORE STATE & ACTIONS
// ============================================================================

interface StageState {
  // Current stage
  currentIndex: number
  currentStage: StageAnchor

  // Transition state
  isTransitioning: boolean
  transitionProgress: number // 0-1 during transition

  // Scroll accumulator for smooth scrolling
  scrollAccumulator: number

  // Navigation cooldown
  lastNavigationTime: number
  cooldownMs: number

  // Derived camera values (updated by CameraController)
  currentCameraPosition: Vector3
  currentCameraTarget: Vector3

  // Actions
  goToStage: (index: number) => void
  nextStage: () => void
  prevStage: () => void
  handleScroll: (deltaY: number) => boolean
  handleSwipe: (deltaX: number, deltaY: number) => boolean
  setTransitioning: (isTransitioning: boolean) => void
  updateCameraPosition: (position: Vector3, target: Vector3) => void

  // Queries
  isStageActive: (id: string) => boolean
  getStageById: (id: string) => StageAnchor | undefined
  getStageIndex: (id: string) => number
}

// ============================================================================
// ZUSTAND STORE
// ============================================================================

export const useStageStore = create<StageState>()(
  subscribeWithSelector((set, get) => ({
    // Initial state
    currentIndex: 0,
    currentStage: STAGE_ANCHORS[0],
    isTransitioning: false,
    transitionProgress: 0,
    scrollAccumulator: 0,
    lastNavigationTime: 0,
    cooldownMs: 450, // Fast enough for snappy swiping, prevents double-trigger

    currentCameraPosition: new Vector3(...STAGE_ANCHORS[0].cameraPosition),
    currentCameraTarget: new Vector3(...STAGE_ANCHORS[0].cameraTarget),

    // Actions
    goToStage: (index: number) => {
      const { currentIndex, isTransitioning, lastNavigationTime, cooldownMs } = get()

      // Bounds check
      if (index < 0 || index >= STAGE_ANCHORS.length) return
      if (index === currentIndex) return

      // Cooldown check
      const now = Date.now()
      if (now - lastNavigationTime < cooldownMs) return
      if (isTransitioning) return

      set({
        currentIndex: index,
        currentStage: STAGE_ANCHORS[index],
        isTransitioning: true,
        transitionProgress: 0,
        lastNavigationTime: now,
        scrollAccumulator: 0,
      })

      // Auto-clear transitioning after animation completes
      setTimeout(() => {
        set({ isTransitioning: false, transitionProgress: 1 })
      }, cooldownMs)
    },

    nextStage: () => {
      const { currentIndex, goToStage } = get()
      goToStage((currentIndex + 1) % STAGE_ANCHORS.length)
    },

    prevStage: () => {
      const { currentIndex, goToStage } = get()
      goToStage((currentIndex - 1 + STAGE_ANCHORS.length) % STAGE_ANCHORS.length)
    },

    handleScroll: (deltaY: number) => {
      const { isTransitioning, lastNavigationTime, cooldownMs, nextStage, prevStage } = get()

      const now = Date.now()
      if (now - lastNavigationTime < cooldownMs) return false
      if (isTransitioning) return false

      // Threshold for scroll activation
      const threshold = 30
      if (Math.abs(deltaY) < threshold) return false

      if (deltaY > 0) {
        nextStage()
      } else {
        prevStage()
      }

      return true
    },

    handleSwipe: (deltaX: number, deltaY: number) => {
      const { isTransitioning, lastNavigationTime, cooldownMs, nextStage, prevStage } = get()

      const now = Date.now()
      if (now - lastNavigationTime < cooldownMs) return false
      if (isTransitioning) return false

      const threshold = 50
      const absX = Math.abs(deltaX)
      const absY = Math.abs(deltaY)

      // Vertical swipe priority
      if (absY > absX && absY > threshold) {
        if (deltaY < 0) {
          nextStage() // Swipe up = next
        } else {
          prevStage() // Swipe down = prev
        }
        return true
      }

      // Horizontal swipe
      if (absX > absY && absX > threshold) {
        if (deltaX < 0) {
          nextStage() // Swipe left = next
        } else {
          prevStage() // Swipe right = prev
        }
        return true
      }

      return false
    },

    setTransitioning: (isTransitioning: boolean) => {
      set({ isTransitioning })
    },

    updateCameraPosition: (position: Vector3, target: Vector3) => {
      set({
        currentCameraPosition: position.clone(),
        currentCameraTarget: target.clone(),
      })
    },

    // Queries
    isStageActive: (id: string) => {
      return get().currentStage.id === id
    },

    getStageById: (id: string) => {
      return STAGE_ANCHORS.find((stage) => stage.id === id)
    },

    getStageIndex: (id: string) => {
      return STAGE_ANCHORS.findIndex((stage) => stage.id === id)
    },
  }))
)

// ============================================================================
// SELECTORS (for optimized subscriptions)
// ============================================================================

export const selectCurrentStage = (state: StageState) => state.currentStage
export const selectCurrentIndex = (state: StageState) => state.currentIndex
export const selectIsTransitioning = (state: StageState) => state.isTransitioning
export const selectCameraPosition = (state: StageState) => state.currentCameraPosition
export const selectCameraTarget = (state: StageState) => state.currentCameraTarget

// ============================================================================
// HOOKS FOR SPECIFIC USE CASES
// ============================================================================

/**
 * Hook to check if a specific stage is active
 */
export function useIsStageActive(stageId: string): boolean {
  return useStageStore((state) => state.currentStage.id === stageId)
}

/**
 * Hook to get camera pose for current stage (with shallow comparison)
 */
export function useStageCameraPose() {
  return useStageStore(
    useShallow((state) => ({
      position: state.currentStage.cameraPosition,
      target: state.currentStage.cameraTarget,
      isTransitioning: state.isTransitioning,
    }))
  )
}

/**
 * Hook for navigation controls (with shallow comparison)
 */
export function useStageNavigation() {
  return useStageStore(
    useShallow((state) => ({
      currentIndex: state.currentIndex,
      currentStage: state.currentStage,
      totalStages: STAGE_ANCHORS.length,
      goToStage: state.goToStage,
      nextStage: state.nextStage,
      prevStage: state.prevStage,
      handleScroll: state.handleScroll,
      handleSwipe: state.handleSwipe,
    }))
  )
}

/**
 * Hook for avatar positioning (with shallow comparison)
 */
export function useAvatarPose() {
  return useStageStore(
    useShallow((state) => ({
      position: state.currentStage.avatarPosition ?? [0, -0.8, 2],
      rotation: state.currentStage.avatarRotation ?? 0,
      isTransitioning: state.isTransitioning,
    }))
  )
}

export default useStageStore
